import { AddCircle, Check, Edit } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteShow } from '~/api/shows';
import { useCreateShowInstance } from '~/api/shows/create-instance';
import { GridActionButton } from '~/components/GridActionButton';
import { DeleteButton as BaseDeleteButton } from '~/components/button';
import { ReadOnlyDataGrid } from '~/components/data-grid';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { assert } from '~/lib/assert';
import type {
  NetworkSchedulerDeviceGroups__DeviceGroup as DeviceGroup,
  NetworkSchedulerEvent__Event as Event,
  NetworkSchedulerDeviceGroups_Show as Show,
  NetworkSchedulerShowEventTypeDeviceGroup__ShowEventTypeDeviceGroup as ShowEventTypeDeviceGroup,
} from '../queries/event.generated';

const getShowInstance = (deviceGroupId: number, event: Event) =>
  event.showDeviceGroups?.find((x) => x.deviceGroup.id === deviceGroupId)?.show;

const DeleteButton = ({
  deviceGroup,
  event,
  ...cellProps
}: { deviceGroup: DeviceGroup; event: Event } & GridActionCellProps) => {
  const { t } = useTranslation();
  const [deleteShow] = useDeleteShow();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const { enqueueSnackbar } = useSnackbar();

  // See if we already have a show instance for the event for the device group
  const instanceShow = getShowInstance(deviceGroup.id, event);

  const onClick = async () => {
    if (!instanceShow) return;

    if (!(await confirmDelete())) return;

    // Delete the instance
    await deleteShow({
      variables: {
        showIds: instanceShow.id,
      },
      onCompleted: () => {
        enqueueSnackbar(
          <Typography color="inherit">
            {t('pages.events.dialog.success.override_removed', {
              eventName: event.name,
              deviceGroupName: deviceGroup.name,
            })}
          </Typography>,
          {
            variant: 'success',
          },
        );
      },
    });
  };

  return (
    <>
      <GridActionButton
        Button={BaseDeleteButton}
        disabled={!instanceShow?.canDestroy.value}
        size="small"
        sx={{ ml: 1 }}
        tooltip={
          instanceShow
            ? instanceShow.canDestroy.value
              ? t('pages.events.dialog.table.tooltips.remove_override')
              : t('pages.events.dialog.table.tooltips.no_permission_remove')
            : t('pages.events.dialog.table.tooltips.no_override_exists')
        }
        variant="contained"
        {...mergeGridActionProps({ onClick }, cellProps)}
      >
        {t('pages.events.dialog.table.actions.remove_override')}
      </GridActionButton>
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.delete')}
        cancel={t('common.dialogs.cancel')}
        deleteConfirm
        prompt={
          <Trans
            i18nKey="pages.events.dialog.confirm_delete.message"
            values={{
              eventName: event.name,
              deviceGroupName: deviceGroup.name,
            }}
            components={{ b: <strong /> }}
          />
        }
        title={t('pages.events.dialog.confirm_delete.title')}
      />
    </>
  );
};

const UpdateButton = ({
  deviceGroup,
  event,
  show,
  ...cellProps
}: {
  deviceGroup: DeviceGroup;
  event: Event;
  show: Show;
} & GridActionCellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [createShowInstance, { loading }] = useCreateShowInstance();

  const [confirmInstance, confirmInstanceProps] = useConfirmDialog();

  // See if we already have a show instance for the event for the device group
  const instanceShow = getShowInstance(deviceGroup.id, event);

  // Call mutation to create the instance
  const createInstance = async () => {
    // Create the instance
    const { data } = await createShowInstance({
      variables: {
        input: { deviceGroupId: deviceGroup.id, eventId: event.id, showId: show.id },
      },
    });

    const newInstanceShow = data?.createShowInstance?.show;

    // Bad stuff if the instance doesn't exist
    assert(newInstanceShow != null, `Instance show not created`);

    navigate(`shows/${newInstanceShow.showFolderId}/show/${newInstanceShow.id}/design`);
  };

  const onClick = async () => {
    // If we have a show instance use it
    if (instanceShow) {
      navigate(`shows/${instanceShow.showFolderId}/show/${instanceShow.id}/design`);
      return;
    }

    // Only display confirmation if a show instance does not exist
    if (!(await confirmInstance())) return;
  };

  return (
    <>
      <GridActionButton
        color={instanceShow ? 'primary' : 'success'}
        disabled={!show.canUpdate.value}
        size="small"
        startIcon={instanceShow ? <Edit /> : <AddCircle />}
        tooltip={
          show.canUpdate.value
            ? instanceShow
              ? t('pages.events.dialog.table.tooltips.update_override')
              : t('pages.events.dialog.table.tooltips.create_override')
            : t('pages.events.dialog.table.tooltips.no_permission_create')
        }
        variant="contained"
        {...mergeGridActionProps({ onClick }, cellProps)}
      >
        {instanceShow
          ? t('pages.events.dialog.table.actions.update_override')
          : t('pages.events.dialog.table.actions.create_override')}
      </GridActionButton>
      <ConfirmDialog
        {...confirmInstanceProps}
        cancel={t('common.dialogs.cancel')}
        confirmButton={
          <LoadingButton
            loadingPosition="start"
            loading={loading}
            color="primary"
            startIcon={<Check />}
            variant="contained"
            onClick={async () => {
              await createInstance();
              confirmInstanceProps.onConfirm();
            }}
          >
            {t('pages.events.dialog.confirm_override.proceed')}
          </LoadingButton>
        }
        prompt={
          <Trans
            i18nKey="pages.events.dialog.confirm_override.message"
            values={{
              eventName: event.name,
              deviceGroupName: deviceGroup.name,
            }}
            components={{ b: <strong /> }}
          />
        }
        title={t('pages.events.dialog.confirm_override.title')}
      />
    </>
  );
};

const useColumns = ({ event, t }: { event: Event; t: (key: string) => string }): GridColDef[] => {
  return useMemo(
    () => [
      {
        field: 'Name',
        headerName: t('common.labels.name'),
        flex: 0.5,
        renderCell: ({ row }: GridRenderCellParams<ShowEventTypeDeviceGroup>) => {
          return row.deviceGroup.name;
        },
      },
      {
        field: 'scheduledShow',
        headerName: t('pages.events.dialog.table.columns.scheduled_show'),
        flex: 0.5,
        renderCell: ({ row }: GridRenderCellParams<ShowEventTypeDeviceGroup>) => {
          return (getShowInstance(row.id, event) || row.show).name;
        },
      },
      {
        field: 'deviceCount',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }: GridRenderCellParams<ShowEventTypeDeviceGroup>) => {
          return <Chip color="primary" label={row.deviceGroup.deviceCount || 0} />;
        },
        minWidth: 100,
      },
      {
        field: 'actions',
        headerName: t('pages.events.dialog.table.columns.actions'),
        type: 'actions',
        align: 'right',
        flex: 0.5,
        getActions: ({ row }: GridRowParams<ShowEventTypeDeviceGroup>) => {
          return [
            <UpdateButton deviceGroup={row.deviceGroup} event={event} key={0} show={row.show} />,
            <DeleteButton deviceGroup={row.deviceGroup} event={event} key={1} />,
          ];
        },
      },
    ],
    [event, t],
  );
};

export interface DeviceGroupsTableProps {
  showEventTypeDeviceGroups: readonly ShowEventTypeDeviceGroup[];
  event: Event;
}

export const DeviceGroupsTable = ({ showEventTypeDeviceGroups, event }: DeviceGroupsTableProps) => {
  const { t } = useTranslation();
  const columns = useColumns({ event, t });
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      actions: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ReadOnlyDataGrid
      columns={columns}
      columnVisibilityModel={showColumns}
      onColumnVisibilityModelChange={setShowColumns}
      rows={showEventTypeDeviceGroups}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: () => (
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="subtitle1">
              {t('pages.events.dialog.table.empty_state')}
            </Typography>
          </Box>
        ),
      }}
    />
  );
};
